import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { TableHead, TableWrapper } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty, isPieceUom, qtyInputStep } from '../lib/quantity';
import { WithWrite } from '../lib/rbac';
import {
  ordersApi,
  shipmentsApi,
  type OrderShipmentCandidate,
  type OrdersMeta,
  type SalesOrderDetail,
  type SalesOrderFormData,
  type ShipmentCreatePayload,
  type ShipmentDetail,
  type ShipmentListItem,
} from '../services/orders';

type OrderFormRow = {
  productId: number | '';
  productSearch: string;
  qty: string;
  uomItemId: number | '';
  notes: string;
};

type ShipmentFormRow = {
  orderRowId: number;
  productLabel: string;
  productId: number;
  uomLabel: string;
  remainingQty: number;
  qtyShipped: string;
  notes: string;
  batchCode: string;
  containerId: number | '';
  allowedProducts: Array<{ id: number; parentProductId: number | null; sku: string | null; name: string }>;
  candidates: OrderShipmentCandidate[];
};

type ShipmentFormMode = 'create' | 'edit';
type SnapshotPriceConfirmState = {
  rowId: number;
  productLabel: string;
  previewPrice: number | null;
  currentSnapshotPrice: number | null;
  priceSourceLabel: string;
  priceListName: string | null;
};

const EMPTY_FORM: SalesOrderFormData = {
  customerId: null,
  status: 'DRAFT',
  orderDate: '',
  requestedDeliveryDate: null,
  notes: '',
  rows: [],
};

const EMPTY_ROW: OrderFormRow = {
  productId: '',
  productSearch: '',
  qty: '',
  uomItemId: '',
  notes: '',
};

const REQUESTED_DELIVERY_SUMMARY_LABEL = 'Önskad leverans';
const REQUESTED_DELIVERY_FORM_LABEL = 'Önskat leveransdatum';

function formatDateOnly(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '-';
}

function formatPrice(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const amount = Number(value);
  if (!Number.isFinite(amount)) {
    return String(value);
  }
  return `${amount.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

function orderRowPriceSourceLabel(source: SalesOrderDetail['rows'][number]['pricePreview'] extends infer T
  ? T extends { source: infer S }
    ? S
    : never
  : never): string {
  switch (source) {
    case 'CUSTOMER_ROW':
      return 'Kundpris';
    case 'DEFAULT_PRICE_LIST':
      return 'Standardprislista';
    case 'PRODUCT_DEFAULT':
      return 'Produktens standardpris';
    default:
      return 'Saknas';
  }
}

function roundShipmentQty(value: number): number {
  return Number(value.toFixed(3));
}

function productOptionLabel(product: OrdersMeta['products'][number]): string {
  return product.sku ? `${product.sku} - ${product.name}` : product.name;
}

function isDraftShipment(shipment: { ledgerTxId: number | null }): boolean {
  return !shipment.ledgerTxId || shipment.ledgerTxId <= 0;
}

export function OrderDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const orderId = Number(params.id);
  const [meta, setMeta] = useState<OrdersMeta | null>(null);
  const [selected, setSelected] = useState<SalesOrderDetail | null>(null);
  const [shipments, setShipments] = useState<ShipmentListItem[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showShipForm, setShowShipForm] = useState(false);
  const [shipmentFormMode, setShipmentFormMode] = useState<ShipmentFormMode>('create');
  const [form, setForm] = useState<SalesOrderFormData>(EMPTY_FORM);
  const [formRows, setFormRows] = useState<OrderFormRow[]>([]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);
  const [shipmentNumber, setShipmentNumber] = useState('');
  const [shipmentInvoiceNumber, setShipmentInvoiceNumber] = useState('');
  const [shipmentDate, setShipmentDate] = useState('');
  const [shipmentSiteId, setShipmentSiteId] = useState<number | ''>('');
  const [shipmentNotes, setShipmentNotes] = useState('');
  const [shipmentRows, setShipmentRows] = useState<ShipmentFormRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [postingShipment, setPostingShipment] = useState(false);
  const [sendingFortnoxInvoice, setSendingFortnoxInvoice] = useState(false);
  const [showFortnoxWarningDialog, setShowFortnoxWarningDialog] = useState(false);
  const [fortnoxWarnings, setFortnoxWarnings] = useState<string[]>([]);
  const [productDialogProductId, setProductDialogProductId] = useState<number | null>(null);
  const [snapshotPriceConfirm, setSnapshotPriceConfirm] = useState<SnapshotPriceConfirmState | null>(null);
  const [applyingSnapshotPriceRowId, setApplyingSnapshotPriceRowId] = useState<number | null>(null);
  const toast = useToast();
  const { canRead, canWrite } = useAuth();

  if (!canRead('orders')) {
    return <Navigate to="/" replace />;
  }
  if (!Number.isFinite(orderId) || orderId <= 0) {
    return <Navigate to="/orders" replace />;
  }

  const loadShipments = useCallback(async (id: number) => {
    const listRes = await shipmentsApi.list(id);
    setShipments(listRes.shipments);
    if (listRes.shipments.length > 0) {
      const detail = await shipmentsApi.get(listRes.shipments[0].id);
      setSelectedShipment(detail);
    } else {
      setSelectedShipment(null);
    }
  }, []);

  const loadOrder = useCallback(async () => {
    try {
      const [metaRes, detail] = await Promise.all([ordersApi.meta(), ordersApi.get(orderId)]);
      setMeta(metaRes);
      setSelected(detail);
      await loadShipments(orderId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda order');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [loadShipments, navigate, orderId, toast]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const shipmentRowsRemaining = useMemo(
    () =>
      selected?.rows
        .map((row) => {
          const orderedQty = Number(row.qty);
          const shippedQty = Number(row.shippedQty ?? 0);
          return { ...row, remainingQty: Math.max(0, roundShipmentQty(orderedQty - shippedQty)) };
        })
        .filter((row) => row.remainingQty > 0) ?? [],
    [selected]
  );

  const selectedShipmentQtyByOrderRow = useMemo(() => {
    const totals = new Map<number, number>();
    for (const row of selectedShipment?.rows ?? []) {
      if (!row.orderRowId) {
        continue;
      }
      totals.set(row.orderRowId, roundShipmentQty((totals.get(row.orderRowId) ?? 0) + Number(row.qtyShipped ?? 0)));
    }
    return totals;
  }, [selectedShipment]);

  const createShipmentRowsFromOrder = (rows: SalesOrderDetail['rows']) =>
    rows.map((row) => {
      const selectedUomLabel = meta?.uoms.find((uom) => uom.id === row.uomItemId)?.label ?? row.uomLabel ?? null;
      return {
        orderRowId: row.id ?? 0,
        productLabel: `${row.productSku ? `${row.productSku} - ` : ''}${row.productName ?? row.description ?? ''}`,
        productId: row.productId,
        uomLabel: selectedUomLabel ?? '',
        remainingQty: Math.max(0, roundShipmentQty(Number(row.qty) - Number(row.shippedQty ?? 0))),
        qtyShipped: formatQty(Math.max(0, roundShipmentQty(Number(row.qty) - Number(row.shippedQty ?? 0))), selectedUomLabel),
        notes: '',
        batchCode: '',
        containerId: '',
        allowedProducts:
          row.allowedShipmentProducts?.map((product) => ({
            id: product.id,
            parentProductId: product.parentProductId,
            sku: product.sku,
            name: product.name,
          })) ?? [],
        candidates: row.shipmentCandidates ?? [],
      } satisfies ShipmentFormRow;
    });

  const createShipmentRowsFromDetail = (shipment: ShipmentDetail) => {
    const orderRowMap = new Map((selected?.rows ?? []).map((row) => [row.id ?? 0, row]));
    const shipmentTotalsByOrderRow = new Map<number, number>();
    const draftShipment = isDraftShipment(shipment);
    for (const row of shipment.rows) {
      if (!row.orderRowId) {
        continue;
      }
      shipmentTotalsByOrderRow.set(row.orderRowId, roundShipmentQty((shipmentTotalsByOrderRow.get(row.orderRowId) ?? 0) + Number(row.qtyShipped)));
      }
    const buildShipmentFormRow = (row: ShipmentDetail['rows'][number]) => {
      const orderRow = orderRowMap.get(row.orderRowId ?? 0);
      const selectedUomLabel = orderRow ? (meta?.uoms.find((uom) => uom.id === orderRow.uomItemId)?.label ?? orderRow.uomLabel ?? null) : (row.uomLabel ?? null);
      const baseRemaining = orderRow ? Math.max(0, roundShipmentQty(Number(orderRow.qty) - Number(orderRow.shippedQty ?? 0))) : Number(row.qtyShipped);
      const remainingQtyOverride = orderRow
        ? (draftShipment
          ? baseRemaining
          : roundShipmentQty(baseRemaining + (shipmentTotalsByOrderRow.get(row.orderRowId ?? 0) ?? Number(row.qtyShipped))))
        : Number(row.qtyShipped);
      return {
        orderRowId: row.orderRowId ?? 0,
        productLabel: orderRow ? `${orderRow.productSku ? `${orderRow.productSku} - ` : ''}${orderRow.productName ?? orderRow.description ?? ''}` : `${row.productSku ? `${row.productSku} - ` : ''}${row.productName ?? ''}`,
        productId: row.productId,
        uomLabel: selectedUomLabel ?? '',
        remainingQty: remainingQtyOverride,
        qtyShipped: (row.batchCode ?? '').trim() !== '' || row.containerId !== null
          ? formatQty(row.qtyShipped, selectedUomLabel)
          : '0',
        notes: row.notes ?? '',
        batchCode: row.batchCode ?? '',
        containerId: row.containerId ?? '',
        allowedProducts:
          orderRow?.allowedShipmentProducts?.map((product) => ({
            id: product.id,
            parentProductId: product.parentProductId,
            sku: product.sku,
            name: product.name,
          })) ?? [],
        candidates: orderRow?.shipmentCandidates ?? [],
      } satisfies ShipmentFormRow;
    };

    const shipmentRowsByOrderRow = new Map<number, ShipmentDetail['rows']>();
    for (const row of shipment.rows) {
      const orderRowId = row.orderRowId ?? 0;
      const existing = shipmentRowsByOrderRow.get(orderRowId) ?? [];
      existing.push(row);
      shipmentRowsByOrderRow.set(orderRowId, existing);
    }

    const mergedRows: ShipmentFormRow[] = [];
    for (const orderRow of selected?.rows ?? []) {
      const existingRows = shipmentRowsByOrderRow.get(orderRow.id ?? 0) ?? [];
      if (existingRows.length > 0) {
        for (const shipmentRow of existingRows) {
          mergedRows.push(buildShipmentFormRow(shipmentRow));
        }
        continue;
      }

      mergedRows.push({
        orderRowId: orderRow.id ?? 0,
        productLabel: `${orderRow.productSku ? `${orderRow.productSku} - ` : ''}${orderRow.productName ?? orderRow.description ?? ''}`,
        productId: orderRow.productId,
        uomLabel: meta?.uoms.find((uom) => uom.id === orderRow.uomItemId)?.label ?? orderRow.uomLabel ?? '',
        remainingQty: Math.max(0, roundShipmentQty(Number(orderRow.qty) - Number(orderRow.shippedQty ?? 0))),
        qtyShipped: '0',
        notes: '',
        batchCode: '',
        containerId: '',
        allowedProducts:
          orderRow.allowedShipmentProducts?.map((product) => ({
            id: product.id,
            parentProductId: product.parentProductId,
            sku: product.sku,
            name: product.name,
          })) ?? [],
        candidates: orderRow.shipmentCandidates ?? [],
      } satisfies ShipmentFormRow);
    }

    return mergedRows;
  };

  const resetShipmentRowFromOrder = (
    entry: ShipmentFormRow,
    orderRow: SalesOrderDetail['rows'][number],
    remainingQtyOverride?: number
  ): ShipmentFormRow => {
    const selectedUomLabel = meta?.uoms.find((uom) => uom.id === orderRow.uomItemId)?.label ?? orderRow.uomLabel ?? '';
    const remainingQty = remainingQtyOverride ?? Math.max(0, roundShipmentQty(Number(orderRow.qty) - Number(orderRow.shippedQty ?? 0)));
    const currentQty = Number(entry.qtyShipped);
    const qtyShipped = currentQty > 0 && currentQty <= remainingQty ? formatQty(currentQty, selectedUomLabel) : formatQty(remainingQty, selectedUomLabel);
    return {
      ...entry,
      orderRowId: orderRow.id ?? entry.orderRowId,
      productLabel: `${orderRow.productSku ? `${orderRow.productSku} - ` : ''}${orderRow.productName ?? orderRow.description ?? ''}`,
      productId: orderRow.productId,
      uomLabel: selectedUomLabel,
      remainingQty,
      qtyShipped,
      batchCode: '',
      containerId: '',
      allowedProducts:
        orderRow.allowedShipmentProducts?.map((product) => ({
          id: product.id,
          parentProductId: product.parentProductId,
          sku: product.sku,
          name: product.name,
        })) ?? [],
      candidates: orderRow.shipmentCandidates ?? [],
    };
  };

  const getShipmentRowAvailableQty = useCallback(
    (rows: ShipmentFormRow[], index: number) => {
      const currentRow = rows[index];
      if (!currentRow) {
        return 0;
      }
      const allocatedToProduct = rows.reduce((sum, row) => {
        if (row.productId !== currentRow.productId) {
          return sum;
        }
        return sum + (Number(row.qtyShipped) || 0);
      }, 0);
      return Math.max(0, roundShipmentQty(currentRow.remainingQty - allocatedToProduct));
    },
    []
  );

  const addShipmentBatchRow = (index: number) => {
    setShipmentRows((current) => {
      const currentRow = current[index];
      if (!currentRow) {
        return current;
      }
      const nextRow: ShipmentFormRow = {
        ...currentRow,
        qtyShipped: '',
        notes: '',
        batchCode: '',
        containerId: '',
      };
      return [
        ...current.slice(0, index + 1),
        nextRow,
        ...current.slice(index + 1),
      ];
    });
    window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(`[data-shipment-row="${index + 1}"]`);
      row?.scrollIntoView({ block: 'center' });
    }, 0);
  };

  const updateShipmentRowOrder = (index: number, orderRowId: number) => {
    if (!selected || !meta) return;
    const orderRow = selected.rows.find((row) => row.id === orderRowId);
    if (!orderRow) return;
    const baseRemainingQty = Math.max(0, roundShipmentQty(Number(orderRow.qty) - Number(orderRow.shippedQty ?? 0)));
    const editableRemainingQty =
      shipmentFormMode === 'edit' && selectedShipment && !isDraftShipment(selectedShipment)
        ? roundShipmentQty(baseRemainingQty + (selectedShipmentQtyByOrderRow.get(orderRowId) ?? 0))
        : baseRemainingQty;
    setShipmentRows((current) =>
      current.map((entry, rowIndex) => (
        rowIndex === index ? resetShipmentRowFromOrder(entry, orderRow, editableRemainingQty) : entry
      ))
    );
  };

  const removeShipmentRow = (index: number) => {
    setShipmentRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
  };

  const updateFormRow = (index: number, patch: Partial<OrderFormRow>) => {
    setFormRows((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))
    );
  };

  const addFormRow = () => {
    const nextIndex = formRows.length;
    setFormRows((current) => [...current, { ...EMPTY_ROW }]);
    setOpenProductSearchIndex(nextIndex);
    window.setTimeout(() => {
      const row = document.querySelector<HTMLElement>(`[data-order-row="${nextIndex}"]`);
      row?.scrollIntoView({ block: 'center' });
      const input = document.querySelector<HTMLInputElement>(`[data-order-row-product="${nextIndex}"]`);
      input?.focus();
      input?.select();
    }, 0);
  };

  const removeFormRow = (index: number) => {
    setFormRows((current) => current.filter((_, rowIndex) => rowIndex !== index));
    setOpenProductSearchIndex((current) => (current === index ? null : current));
  };

  const handleProductSelect = (index: number, productId: number) => {
    if (!meta) return;
    const product = meta.products.find((item) => item.id === productId);
    if (!product) return;
    updateFormRow(index, {
      productId,
      productSearch: productOptionLabel(product),
      uomItemId: product.defaultUomItemId ?? '',
    });
    setOpenProductSearchIndex(null);
  };

  const openEdit = () => {
    if (!selected || !meta) return;
    setForm({
      customerId: selected.customerId,
      status: selected.status,
      orderDate: selected.orderDate,
      requestedDeliveryDate: selected.requestedDeliveryDate,
      notes: selected.notes ?? '',
      rows: selected.rows.map((row) => ({
        productId: row.productId,
        qty: row.qty,
        uomItemId: row.uomItemId,
        notes: row.notes ?? '',
      })),
    });
    setFormRows(
      selected.rows.map((row) => {
        const product = meta.products.find((item) => item.id === row.productId);
        const selectedUomLabel = meta.uoms.find((uom) => uom.id === row.uomItemId)?.label ?? null;
        return {
          productId: row.productId,
          productSearch: product ? productOptionLabel(product) : row.productName ?? row.description ?? '',
          qty: formatQty(row.qty, selectedUomLabel),
          uomItemId: row.uomItemId ?? '',
          notes: row.notes ?? '',
        };
      })
    );
    setOpenProductSearchIndex(null);
    setShowForm(true);
  };

  const openShip = () => {
    if (!selected || !meta) return;
    const defaultSiteId = meta.sites.find((site) => site.name === 'Lager')?.id ?? meta.sites[0]?.id ?? '';
    setShipmentFormMode('create');
    setShipmentNumber('');
    setShipmentInvoiceNumber('');
    setShipmentDate(new Date().toISOString().slice(0, 16));
    setShipmentSiteId(defaultSiteId);
    setShipmentNotes('');
    setShipmentRows(createShipmentRowsFromOrder(shipmentRowsRemaining));
    setShowShipForm(true);
  };

  const openEditShipment = () => {
    if (!selected || !selectedShipment || !meta) return;
    setShipmentFormMode('edit');
    setShipmentNumber(selectedShipment.shipmentNumber ?? '');
    setShipmentInvoiceNumber(selectedShipment.invoiceNumber ?? '');
    setShipmentDate(selectedShipment.shipDate.slice(0, 16));
    setShipmentSiteId(selectedShipment.siteId);
    setShipmentNotes(selectedShipment.notes ?? '');
    setShipmentRows(createShipmentRowsFromDetail(selectedShipment));
    setShowShipForm(true);
  };

  const handleSave = async () => {
    if (!selected) return;
    const rows = formRows
      .filter((row) => row.productId !== '' && row.qty.trim() !== '')
      .map((row) => ({
        productId: Number(row.productId),
        qty: Number(row.qty),
        uomItemId: row.uomItemId === '' ? null : Number(row.uomItemId),
        notes: row.notes.trim() || null,
      }));

    const payload: SalesOrderFormData = { ...form, orderNumber: selected.orderNumber, rows };

    setSaving(true);
    try {
      await ordersApi.update(orderId, payload);
      toast.success('Order uppdaterad');
      setShowForm(false);
      await loadOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara order');
    } finally {
      setSaving(false);
    }
  };

  const handleShip = async () => {
    if (!selected || shipmentSiteId === '') return;
    const rows = shipmentRows
      .map((row) => ({
        orderRowId: row.orderRowId,
        productId: row.productId,
        qtyShipped: Number(row.qtyShipped),
        batchCode: row.batchCode.trim() || null,
        containerId: row.containerId === '' ? null : Number(row.containerId),
        notes: row.notes.trim() || null,
      }))
      .filter((row) => row.qtyShipped > 0);
    const payload: ShipmentCreatePayload = {
      shipmentNumber: shipmentNumber.trim() || null,
      invoiceNumber: shipmentFormMode === 'edit' ? (shipmentInvoiceNumber.trim() || null) : null,
      shipDate: shipmentDate,
      siteId: Number(shipmentSiteId),
      notes: shipmentNotes.trim() || null,
      rows,
    };
    setPostingShipment(true);
    try {
      if (shipmentFormMode === 'edit' && selectedShipment) {
        await shipmentsApi.update(selectedShipment.id, payload);
        toast.success(isDraftShipment(selectedShipment) ? 'Leveransutkast uppdaterat' : 'Leverans uppdaterad');
      } else {
        await ordersApi.ship(orderId, payload);
        toast.success('Leveransutkast sparat');
      }
      setShowShipForm(false);
      await loadOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : (shipmentFormMode === 'edit' ? 'Kunde inte spara leverans' : 'Kunde inte spara leveransutkast'));
    } finally {
      setPostingShipment(false);
    }
  };

  const handlePostShipment = async () => {
    if (!selectedShipment) {
      return;
    }
    setPostingShipment(true);
    try {
      await shipmentsApi.post(selectedShipment.id);
      toast.success('Leverans bokförd');
      await loadOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte bokföra leverans');
    } finally {
      setPostingShipment(false);
    }
  };

  const selectShipment = async (id: number) => {
    try {
      const detail = await shipmentsApi.get(id);
      setSelectedShipment(detail);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda leverans');
    }
  };

  const sendFortnoxInvoice = async (force = false) => {
    if (!selectedShipment) {
      return;
    }
    const shipmentId = selectedShipment.id;
    setSendingFortnoxInvoice(true);
    try {
      const result = await shipmentsApi.createFortnoxInvoice(shipmentId, force);
      if (result.requiresConfirmation && result.warnings?.length) {
        setFortnoxWarnings(result.warnings);
        setShowFortnoxWarningDialog(true);
        toast.info('Kontrollera fakturavarningarna');
        return;
      }

      setShowFortnoxWarningDialog(false);
      setFortnoxWarnings([]);
      toast.success(`Faktura ${result.invoiceNumber ?? ''} skickad till Fortnox`.trim());
      await loadOrder();
      await selectShipment(shipmentId);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skicka faktura till Fortnox');
    } finally {
      setSendingFortnoxInvoice(false);
    }
  };

  const handleSendFortnoxInvoice = () => {
    void sendFortnoxInvoice(false);
  };

  const handleConfirmFortnoxInvoice = () => {
    void sendFortnoxInvoice(true);
  };

  const handlePrintDeliveryNote = () => {
    if (!selectedShipment) {
      toast.error('Välj en leverans först');
      return;
    }
    window.open(`/print/orders/${orderId}/foljesedel/${selectedShipment.id}`, '_blank', 'noopener,noreferrer');
  };

  const closeProductDialog = () => {
    setProductDialogProductId(null);
    void loadOrder();
  };

  const confirmApplySnapshotPrice = (row: SalesOrderDetail['rows'][number]) => {
    setSnapshotPriceConfirm({
      rowId: row.id ?? 0,
      productLabel: `${row.productSku ? `${row.productSku} - ` : ''}${row.productName ?? row.description ?? ''}`,
      previewPrice: row.pricePreview?.unitPriceExVat ?? null,
      currentSnapshotPrice: row.unitPriceExVat ?? null,
      priceSourceLabel: orderRowPriceSourceLabel(row.pricePreview?.source ?? 'NONE'),
      priceListName: row.pricePreview?.priceListName ?? null,
    });
  };

  const handleApplySnapshotPrice = async () => {
    if (!snapshotPriceConfirm) {
      return;
    }
    setApplyingSnapshotPriceRowId(snapshotPriceConfirm.rowId);
    try {
      const result = await ordersApi.applySnapshotPrice(snapshotPriceConfirm.rowId);
      toast.success(
        `Snapshotpris satt till ${formatPrice(result.unitPriceExVat)} exkl. moms${result.priceListName ? ` från ${result.priceListName}` : ''}`
      );
      setSnapshotPriceConfirm(null);
      await loadOrder();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera snapshotpris');
    } finally {
      setApplyingSnapshotPriceRowId(null);
    }
  };

  if (loading || !meta || !selected) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm text-gray-500">
            <Link to="/orders" className="hover:underline">
              Kundorder
            </Link>{' '}
            / {selected.orderNumber}
          </div>
          <h1 className="text-lg font-semibold">{selected.orderNumber}</h1>
          <p className="text-sm text-gray-600">{selected.customerName ?? 'Ingen kund'}</p>
        </div>
        <div className="flex gap-2">
          <WithWrite moduleCode="orders">
            {shipmentRowsRemaining.length > 0 &&
              selected.status !== 'DRAFT' &&
              selected.status !== 'CANCELLED' && (
                <button
                  type="button"
                  onClick={openShip}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover"
                >
                  Ny leverans
                </button>
              )}
          </WithWrite>
          <WithWrite moduleCode="orders">
            {shipments.length === 0 && (
              <button
                type="button"
                onClick={openEdit}
                className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
              >
                Redigera
              </button>
            )}
          </WithWrite>
        </div>
      </div>

      <div className="space-y-2 rounded border border-gray-200 bg-white p-3 text-sm">
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <div><span className="text-gray-500">Status:</span> {selected.status}</div>
          <div><span className="text-gray-500">Datum:</span> {formatDateOnly(selected.orderDate)}</div>
          <div><span className="text-gray-500">{REQUESTED_DELIVERY_SUMMARY_LABEL}:</span> {formatDateOnly(selected.requestedDeliveryDate)}</div>
          <div><span className="text-gray-500">Rader:</span> {selected.rows.length}</div>
        </div>
        <div className="flex gap-2">
          <div className="shrink-0 text-gray-500">Anteckningar:</div>
          <div className="min-w-0 whitespace-pre-wrap">{selected.notes ?? '-'}</div>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="font-medium">Orderrader</h2>
        <TableWrapper>
          <TableHead>
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
              <th className="px-2 py-1.5 text-left font-medium">Prisindikering</th>
              <th className="px-2 py-1.5 text-left font-medium">Beställt</th>
              <th className="px-2 py-1.5 text-left font-medium">Levererat</th>
              <th className="px-2 py-1.5 text-left font-medium">Utkast</th>
              <th className="px-2 py-1.5 text-left font-medium">Kvar</th>
              <th className="px-2 py-1.5 text-left font-medium">Enhet</th>
              <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
            </tr>
          </TableHead>
          <tbody>
            {selected.rows.map((row) => {
              const orderedQty = Number(row.qty);
              const shippedQty = Number(row.shippedQty ?? 0);
              const draftQty = Number(row.draftQty ?? 0);
              const remainingQty = Math.max(0, roundShipmentQty(orderedQty - shippedQty - draftQty));
              const preview = row.pricePreview;
              const snapshotPrice = row.unitPriceExVat ?? null;
              const hasSnapshotPrice = snapshotPrice !== null && snapshotPrice !== undefined;
              const previewPrice = preview?.unitPriceExVat ?? null;
              const showSnapshotDifference =
                hasSnapshotPrice &&
                previewPrice !== null &&
                Math.abs(Number(snapshotPrice) - Number(previewPrice)) >= 0.005;
              return (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="px-2 py-1 align-top">
                    <div className="space-y-1">
                      <div>{row.productSku ? `${row.productSku} - ` : ''}{row.productName ?? row.description}</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <Link
                          to={`/products/${row.productId}`}
                          className="text-primary hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Öppna artikelsida
                        </Link>
                        {canWrite('products') && (
                          <button
                            type="button"
                            onClick={() => setProductDialogProductId(row.productId)}
                            className="text-primary hover:underline"
                          >
                            Öppna i dialog
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1 align-top">
                    <div className="space-y-1 text-sm">
                      <div className={`font-medium ${previewPrice === null ? 'text-red-600' : 'text-gray-900'}`}>
                        {previewPrice === null ? 'Saknar pris' : `${formatPrice(previewPrice)} exkl. moms`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {orderRowPriceSourceLabel(preview?.source ?? 'NONE')}
                        {preview?.priceListName ? ` • ${preview.priceListName}` : ''}
                        {preview?.vatRateLabel ? ` • ${preview.vatRateLabel}` : ''}
                      </div>
                      {hasSnapshotPrice && (
                        <div className={`text-xs ${showSnapshotDifference ? 'text-amber-700' : 'text-gray-500'}`}>
                          Orderrad: {formatPrice(snapshotPrice)} exkl. moms
                        </div>
                      )}
                      {canWrite('orders') && previewPrice !== null && row.id && (
                        <button
                          type="button"
                          onClick={() => confirmApplySnapshotPrice(row)}
                          className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800 hover:bg-amber-100"
                        >
                          {hasSnapshotPrice ? 'Uppdatera snapshotpris' : 'Sätt snapshotpris på orderrad'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1">{formatQty(orderedQty, row.uomLabel)}</td>
                  <td className="px-2 py-1">{formatQty(shippedQty, row.uomLabel)}</td>
                  <td className="px-2 py-1">{formatQty(draftQty, row.uomLabel)}</td>
                  <td className="px-2 py-1">{formatQty(remainingQty, row.uomLabel)}</td>
                  <td className="px-2 py-1">{row.uomLabel ?? '-'}</td>
                  <td className="px-2 py-1 whitespace-pre-wrap">{row.notes ?? '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </TableWrapper>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-medium">Leveranser</h2>
            <div className="text-xs text-gray-500">{shipments.length} st</div>
          </div>
          <TableWrapper>
            <TableHead>
              <tr>
                <th className="px-2 py-1.5 text-left font-medium">Nr</th>
                <th className="px-2 py-1.5 text-left font-medium">Status</th>
                <th className="px-2 py-1.5 text-left font-medium">FakturaNr</th>
                <th className="px-2 py-1.5 text-left font-medium">Datum</th>
                <th className="px-2 py-1.5 text-left font-medium">Rader</th>
                <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
              </tr>
            </TableHead>
            <tbody>
              {shipments.map((shipment) => (
                <tr
                  key={shipment.id}
                  onClick={() => void selectShipment(shipment.id)}
                  className={`cursor-pointer border-b border-gray-100 hover:bg-gray-50 ${selectedShipment?.id === shipment.id ? 'bg-orange-50' : ''}`}
                >
                  <td className="px-2 py-1 font-medium">{shipment.shipmentNumber ?? `#${shipment.id}`}</td>
                  <td className="px-2 py-1">{isDraftShipment(shipment) ? 'Utkast' : 'Bokförd'}</td>
                  <td className="px-2 py-1">{shipment.invoiceNumber ?? '-'}</td>
                  <td className="px-2 py-1">{formatDateOnly(shipment.shipDate)}</td>
                  <td className="px-2 py-1">{shipment.rowCount}</td>
                  <td className="px-2 py-1">{formatQty(shipment.totalQty)}</td>
                </tr>
              ))}
              {shipments.length === 0 && <tr><td colSpan={6} className="px-2 py-3 text-sm text-gray-500">Ingen leverans registrerad ännu.</td></tr>}
            </tbody>
          </TableWrapper>
        </div>
        <div className="space-y-2">
          <h2 className="font-medium">Leveransdetaljer</h2>
          {!selectedShipment ? (
            <div className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-500">Välj en leverans i listan.</div>
          ) : (
            <div className="space-y-3 rounded border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">Leveransdetaljer</div>
                <div className="flex gap-2">
                  <WithWrite moduleCode="orders">
                    <button type="button" onClick={openEditShipment} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                      {isDraftShipment(selectedShipment) ? 'Redigera utkast' : 'Redigera leverans'}
                    </button>
                  </WithWrite>
                  {isDraftShipment(selectedShipment) ? (
                    <>
                      <button type="button" onClick={handlePrintDeliveryNote} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">
                        Förhandsvisa följesedel
                      </button>
                      <WithWrite moduleCode="orders">
                        <button type="button" onClick={handlePostShipment} disabled={postingShipment} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">
                          {postingShipment ? 'Bokför...' : 'Leverera'}
                        </button>
                      </WithWrite>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={handlePrintDeliveryNote} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">
                        Öppna följesedel
                      </button>
                      <WithWrite moduleCode="orders">
                        {selectedShipment.invoiceNumber ? (
                          <button type="button" disabled className="rounded border border-gray-300 px-2 py-1 text-sm text-gray-500">
                            Faktura skickad
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendFortnoxInvoice}
                            disabled={sendingFortnoxInvoice}
                            className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                          >
                            {sendingFortnoxInvoice ? 'Skickar...' : 'Skicka faktura till Fortnox'}
                          </button>
                        )}
                      </WithWrite>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-gray-500">Leverans</div><div>{selectedShipment.shipmentNumber ?? `#${selectedShipment.id}`}</div></div>
                <div><div className="text-gray-500">Status</div><div>{isDraftShipment(selectedShipment) ? 'Utkast' : 'Bokförd'}</div></div>
                <div><div className="text-gray-500">Datum</div><div>{formatDateOnly(selectedShipment.shipDate)}</div></div>
                <div><div className="text-gray-500">FakturaNr</div><div>{selectedShipment.invoiceNumber ?? '-'}</div></div>
                <div className="col-span-2"><div className="text-gray-500">Anteckningar</div><div className="whitespace-pre-wrap">{selectedShipment.notes ?? '-'}</div></div>
              </div>
              <TableWrapper maxHeight="none">
                <TableHead>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                    <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
                    <th className="px-2 py-1.5 text-left font-medium">Batch</th>
                    <th className="px-2 py-1.5 text-left font-medium">Kärl</th>
                  </tr>
                </TableHead>
                <tbody>
                  {selectedShipment.rows.map((row) => (
                    <tr key={row.id} className="border-b border-gray-100">
                      <td className="px-2 py-1">{row.productSku ? `${row.productSku} - ` : ''}{row.productName ?? '-'}</td>
                      <td className="px-2 py-1">{formatQty(row.qtyShipped, row.uomLabel)} {row.uomLabel ?? ''}</td>
                      <td className="px-2 py-1">{row.batchCode ?? '-'}</td>
                      <td className="px-2 py-1">{formatContainerDisplayId(row.containerId ?? null, row.containerCode ?? null) || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </TableWrapper>
            </div>
          )}
        </div>
      </div>

      {showFortnoxWarningDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-xl rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Fakturavarningar</h2>
                <p className="text-sm text-gray-600">Fakturan kan skickas, men följande rader behöver kontrolleras.</p>
              </div>
              <ul className="space-y-1 rounded border border-info bg-info/10 p-3 text-sm text-gray-800">
                {fortnoxWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowFortnoxWarningDialog(false)}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleConfirmFortnoxInvoice}
                  disabled={sendingFortnoxInvoice}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {sendingFortnoxInvoice ? 'Skickar...' : 'Skicka ändå'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {productDialogProductId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded bg-white shadow-lg">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div>
                <h2 className="text-base font-semibold">Artikeldetalj</h2>
                <p className="text-sm text-gray-600">Stäng dialogen när du har sparat priset så laddas ordern om.</p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to={`/products/${productDialogProductId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary hover:underline"
                >
                  Öppna i ny flik
                </Link>
                <button
                  type="button"
                  onClick={closeProductDialog}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
                >
                  Stäng
                </button>
              </div>
            </div>
            <iframe
              title={`Produkt ${productDialogProductId}`}
              src={`/products/${productDialogProductId}`}
              className="min-h-0 w-full flex-1 bg-white"
            />
          </div>
        </div>
      )}

      {snapshotPriceConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div>
                <h2 className="text-base font-semibold">Sätt snapshotpris</h2>
                <p className="text-sm text-gray-600">
                  Vill du verkligen kopiera in aktuellt pris till orderraden?
                  {snapshotPriceConfirm.currentSnapshotPrice !== null
                    ? ' Befintligt snapshotpris kommer att skrivas över.'
                    : ' Detta sätter snapshotpris på raden för historik.'}
                </p>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-gray-800">
                <div className="font-medium">{snapshotPriceConfirm.productLabel}</div>
                <div>Nuvarande snapshot: {snapshotPriceConfirm.currentSnapshotPrice === null ? 'saknas' : `${formatPrice(snapshotPriceConfirm.currentSnapshotPrice)} exkl. moms`}</div>
                <div>Pris: {snapshotPriceConfirm.previewPrice === null ? 'saknas' : `${formatPrice(snapshotPriceConfirm.previewPrice)} exkl. moms`}</div>
                <div>Källa: {snapshotPriceConfirm.priceSourceLabel}{snapshotPriceConfirm.priceListName ? ` • ${snapshotPriceConfirm.priceListName}` : ''}</div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSnapshotPriceConfirm(null)}
                  disabled={applyingSnapshotPriceRowId === snapshotPriceConfirm.rowId}
                  className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100 disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleApplySnapshotPrice}
                  disabled={applyingSnapshotPriceRowId === snapshotPriceConfirm.rowId}
                  className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50"
                >
                  {applyingSnapshotPriceRowId === snapshotPriceConfirm.rowId ? 'Sätter...' : 'Ja, sätt snapshotpris'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div className="sticky top-0 z-20 -mx-4 border-b border-gray-200 bg-white px-4 pb-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h2 className="text-base font-semibold">Redigera order</h2>
                    <p className="text-xs text-gray-500">{selected.orderNumber}</p>
                  </div>
                  <button type="button" onClick={addFormRow} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover">
                    Lägg till rad
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-600">Ordernummer</label>
                  <input value={selected.orderNumber} readOnly className="w-full bg-gray-50 text-gray-600" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Kund</label>
                  <select value={form.customerId ?? ''} onChange={(event) => setForm((current) => ({ ...current, customerId: event.target.value ? Number(event.target.value) : null }))} className="w-full">
                    <option value="">-</option>
                    {meta.customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Datum *</label>
                  <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={form.orderDate} onChange={(event) => setForm((current) => ({ ...current, orderDate: event.target.value }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">{REQUESTED_DELIVERY_FORM_LABEL}</label>
                  <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={form.requestedDeliveryDate ?? ''} onChange={(event) => setForm((current) => ({ ...current, requestedDeliveryDate: event.target.value || null }))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600">Status</label>
                  <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} className="w-full">
                    {meta.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Orderrader</h3>
                  <div className="text-xs text-gray-500">{formRows.length} st</div>
                </div>
                <div className="max-h-[42vh] overflow-auto rounded border border-gray-200">
                  <table className="min-w-[980px] w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-10 bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                        <th className="w-24 px-2 py-1.5 text-left font-medium">Mängd</th>
                        <th className="w-24 px-2 py-1.5 text-left font-medium">Enhet</th>
                        <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
                        <th className="w-12 px-2 py-1.5 text-left font-medium"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formRows.map((row, index) => {
                        const selectedUomLabel = meta.uoms.find((uom) => uom.id === (row.uomItemId === "" ? null : row.uomItemId))?.label ?? null;
                        const matchingProducts = meta.products.filter((product) => {
                          const search = row.productSearch.trim().toLowerCase();
                          if (!search) {
                            return true;
                          }
                          return `${product.sku ?? ""} ${product.name}`.toLowerCase().includes(search);
                        }).slice(0, 12);

                        return (
                          <tr key={index} data-order-row={index} className="border-b border-gray-100 align-top">
                            <td className="px-2 py-1.5">
                              <div className="relative">
                                <input
                                  data-order-row-product={index}
                                  value={row.productSearch}
                                  placeholder="Sök artikelnummer eller namn"
                                  onFocus={() => setOpenProductSearchIndex(index)}
                                  onChange={(event) => updateFormRow(index, { productId: "", productSearch: event.target.value })}
                                  onBlur={() => {
                                    window.setTimeout(() => {
                                      setOpenProductSearchIndex((current) => current === index ? null : current);
                                      setFormRows((current) => current.map((entry, rowIndex) => {
                                        if (rowIndex !== index || entry.productId === "") {
                                          return entry;
                                        }
                                        const matchedProduct = meta.products.find((product) => product.id === Number(entry.productId));
                                        if (!matchedProduct) {
                                          return entry;
                                        }
                                        return { ...entry, productSearch: productOptionLabel(matchedProduct) };
                                      }));
                                    }, 120);
                                  }}
                                  className="w-full"
                                />
                                {openProductSearchIndex === index && (
                                  <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-gray-200 bg-white shadow-lg">
                                    {matchingProducts.map((product) => (
                                      <button
                                        key={product.id}
                                        type="button"
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          handleProductSelect(index, product.id);
                                        }}
                                        className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
                                      >
                                        {productOptionLabel(product)}
                                      </button>
                                    ))}
                                    {matchingProducts.length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="0"
                                step={qtyInputStep(selectedUomLabel)}
                                value={row.qty}
                                onKeyDown={(event) => {
                                  if (isPieceUom(selectedUomLabel) && (event.key === "." || event.key === ",")) {
                                    event.preventDefault();
                                  }
                                }}
                                onChange={(event) => updateFormRow(index, { qty: event.target.value })}
                                className="w-full"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <select
                                value={row.uomItemId}
                                onChange={(event) => updateFormRow(index, { uomItemId: event.target.value ? Number(event.target.value) : "" })}
                                className="w-full"
                              >
                                <option value="">-</option>
                                {meta.uoms.map((uom) => (
                                  <option key={uom.id} value={uom.id}>{uom.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1.5">
                              <input value={row.notes} onChange={(event) => updateFormRow(index, { notes: event.target.value })} className="w-full" />
                            </td>
                            <td className="px-2 py-1.5">
                              <button
                                type="button"
                                onClick={() => removeFormRow(index)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded border border-red-200 text-red-700 hover:bg-red-50"
                                aria-label="Ta bort rad"
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18" />
                                  <path d="M8 6V4.5h8V6" />
                                  <path d="M6 6l1 14h10l1-14" />
                                  <path d="M10 10v6" />
                                  <path d="M14 10v6" />
                                </svg>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div><label className="block text-sm text-gray-600">Anteckningar</label><textarea value={form.notes ?? ''} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[100px] w-full" /></div>
              <div className="flex gap-2"><button type="button" onClick={handleSave} disabled={saving} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">{saving ? 'Sparar...' : 'Spara'}</button><button type="button" onClick={() => setShowForm(false)} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">Avbryt</button></div>
            </div>
          </div>
        </div>
      )}

      {showShipForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded bg-white p-4 shadow-lg">
            <div className="space-y-3">
              <div><h2 className="text-base font-semibold">{shipmentFormMode === 'edit' ? 'Redigera leverans' : 'Ny leverans'}</h2><p className="text-sm text-gray-600">{selected.orderNumber} | {selected.customerName ?? 'Ingen kund'}</p></div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div><label className="block text-sm text-gray-600">Leveransnummer</label><input value={shipmentNumber || 'Sätts automatiskt'} readOnly className="w-full bg-gray-50 text-gray-600" /></div>
                <div><label className="block text-sm text-gray-600">Leveransdatum *</label><input type="datetime-local" value={shipmentDate} onChange={(event) => setShipmentDate(event.target.value)} className="w-full" /></div>
                {shipmentFormMode === 'edit' && !isDraftShipment(selectedShipment ?? { ledgerTxId: null }) ? (
                  <div><label className="block text-sm text-gray-600">FakturaNr</label><input value={shipmentInvoiceNumber} onChange={(event) => setShipmentInvoiceNumber(event.target.value)} className="w-full" /></div>
                ) : (
                  <div />
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium">Leveransrader</h3>
                  <p className="text-xs text-gray-500">Använd `+` på raden för att lägga till fler batcher på samma artikel.</p>
                </div>
                <TableWrapper maxHeight="42vh">
                  <TableHead>
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Orderrad</th>
                      <th className="px-2 py-1.5 text-left font-medium">Kvar</th>
                      <th className="px-2 py-1.5 text-left font-medium">Mängd</th>
                      <th className="px-2 py-1.5 text-left font-medium">Batch / kärl</th>
                      <th className="px-2 py-1.5 text-left font-medium">Anteckningar</th>
                      <th className="w-12 px-2 py-1.5 text-left font-medium" aria-label="Ta bort rad" />
                    </tr>
                  </TableHead>
                  <tbody>
                    {shipmentRows.map((row, index) => {
                      const availableCandidates = row.candidates;
                      const availableQty = getShipmentRowAvailableQty(shipmentRows, index);
                      return (
                        <tr key={`${row.orderRowId}-${index}`} data-shipment-row={index} className="border-b border-gray-200 align-top last:border-b-0">
                          <td className="px-2 py-1.5 min-w-[16rem]">
                            <select
                              value={row.orderRowId}
                              onChange={(event) => updateShipmentRowOrder(index, Number(event.target.value))}
                              className="w-full"
                            >
                              {selected.rows.map((orderRow) => (
                                <option key={orderRow.id} value={orderRow.id}>
                                  {(orderRow.productSku ? `${orderRow.productSku} - ` : '')}{orderRow.productName ?? orderRow.description ?? ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5 w-[5.5rem] min-w-[5.5rem]">
                            <div className="rounded border border-gray-200 bg-gray-50 px-2 py-2 text-sm">{formatQty(availableQty, row.uomLabel)}</div>
                          </td>
                          <td className="px-2 py-1.5 w-[5.5rem] min-w-[5.5rem]">
                            <input
                              type="text"
                              inputMode={isPieceUom(row.uomLabel) ? 'numeric' : 'decimal'}
                              value={row.qtyShipped}
                              onKeyDown={(event) => {
                                if (isPieceUom(row.uomLabel) && (event.key === '.' || event.key === ',')) event.preventDefault();
                              }}
                              onChange={(event) => setShipmentRows((current) => current.map((entry, rowIndex) => (rowIndex === index ? { ...entry, qtyShipped: event.target.value } : entry)))}
                              className="w-full"
                            />
                          </td>
                          <td className="px-2 py-1.5 min-w-[14rem]">
                            <div className="flex items-center gap-2">
                              <select
                                value={row.containerId !== '' ? `container:${row.containerId}` : row.batchCode ? `batch:${row.batchCode}` : ''}
                                onChange={(event) => {
                                  const value = event.target.value;
                                let batchCode = '';
                                let containerId: number | '' = '';
                                let productId = row.productId;
                                let qtyShipped = '0';
                                if (value.startsWith('container:')) {
                                  containerId = Number(value.slice('container:'.length));
                                  const candidate = availableCandidates.find((item) => item.containerId === containerId);
                                  productId = candidate?.productId ?? row.productId;
                                  batchCode = candidate?.batchCode ?? '';
                                  qtyShipped = candidate ? formatQty(candidate.qtyOnHand, candidate.uomLabel ?? row.uomLabel) : '0';
                                } else if (value.startsWith('batch:')) {
                                  batchCode = value.slice('batch:'.length);
                                  const candidate = availableCandidates.find((item) => item.batchCode === batchCode);
                                  productId = candidate?.productId ?? row.productId;
                                  containerId = candidate?.containerId ?? '';
                                  qtyShipped = candidate ? formatQty(candidate.qtyOnHand, candidate.uomLabel ?? row.uomLabel) : '0';
                                }
                                setShipmentRows((current) => current.map((entry, rowIndex) => (rowIndex === index ? { ...entry, productId, batchCode, containerId, qtyShipped } : entry)));
                              }}
                              className="w-full"
                            >
                                <option value="">Ingen batch vald</option>
                                {availableCandidates.map((candidate, candidateIndex) => {
                                  const key = `${candidate.siteId}-${candidate.containerId ?? 'none'}-${candidate.batchCode ?? 'none'}-${candidateIndex}`;
                                  const value = candidate.containerId ? `container:${candidate.containerId}` : candidate.batchCode ? `batch:${candidate.batchCode}` : '';
                                  const labelParts = [] as string[];
                                  if (candidate.productName && candidate.productId !== row.productId) labelParts.push(candidate.productName);
                                  if (candidate.containerId) labelParts.push(String(candidate.containerId));
                                  else if (candidate.containerCode) labelParts.push(candidate.containerCode);
                                  if (candidate.batchCode && candidate.batchCode !== candidate.containerCode) labelParts.push(`Batch ${candidate.batchCode}`);
                                  labelParts.push(`${formatQty(candidate.qtyOnHand, candidate.uomLabel)} ${candidate.uomLabel ?? ''}`.trim());
                                  return <option key={key} value={value}>{labelParts.join(' | ')}</option>;
                                })}
                              </select>
                              <button
                                type="button"
                                onClick={() => addShipmentBatchRow(index)}
                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-success bg-success/10 text-success hover:bg-success/20"
                                aria-label="Lägg till batchrad under denna rad"
                                title="Lägg till batchrad"
                              >
                                <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 5v14" />
                                  <path d="M5 12h14" />
                                </svg>
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-1.5 min-w-[12rem]">
                            <input
                              value={row.notes}
                              onChange={(event) => setShipmentRows((current) => current.map((entry, rowIndex) => (rowIndex === index ? { ...entry, notes: event.target.value } : entry)))}
                              className="w-full"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              type="button"
                              onClick={() => removeShipmentRow(index)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded border border-red-200 text-red-700 hover:bg-red-50"
                              aria-label="Ta bort rad"
                            >
                              <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 6h18" />
                                <path d="M8 6V4.5h8V6" />
                                <path d="M6 6l1 14h10l1-14" />
                                <path d="M10 10v6" />
                                <path d="M14 10v6" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </TableWrapper>
              </div>
              <div><label className="block text-sm text-gray-600">Anteckningar</label><textarea value={shipmentNotes} onChange={(event) => setShipmentNotes(event.target.value)} className="min-h-[100px] w-full" /></div>
              <div className="flex gap-2"><button type="button" onClick={handleShip} disabled={postingShipment} className="rounded bg-success px-2 py-1 text-sm text-white hover:bg-success-hover disabled:opacity-50">{postingShipment ? 'Sparar...' : (shipmentFormMode === 'edit' ? 'Spara leverans' : 'Spara utkast')}</button><button type="button" onClick={() => setShowShipForm(false)} className="rounded border border-gray-300 px-2 py-1 text-sm hover:bg-gray-100">Avbryt</button></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


